import { useCallback, useEffect, useState } from "react";
import { useWebServer } from "../../../../../providers";

// The organization's collection-site groups (agamotto-db-ms /trap-site-groups)
// and all of its sites to put in them. Group ids and site ids are strings
// here; sites go back as the server stores them. Not `enabled`, it loads
// nothing and both lists stay empty.
export const useSiteGroups = ({ enabled = true } = {}) => {
  const { sendRequest } = useWebServer();
  // the service speaks snake_case; sendRequest snakeifies what we send
  const fetchGroups = async () => {
    const res = await sendRequest("get")({ url: "/trap-site-groups", devPort: 5005 });
    return (res.data?.groups ?? []).map((group) => ({
      id: group.id,
      name: group.name,
      description: group.description ?? "",
      trapSiteIds: (group.trap_site_ids ?? []).map(String),
    }));
  };
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, s] = await Promise.all([
        fetchGroups(),
        sendRequest("get")({ url: "/trap-sites/", params: { pageSize: 1000, page: 0, orderBy: "display_name", order: "asc" }, devPort: 5005 }),
      ]);
      setGroups(g);
      setSites(
        (s.data?.trapSites ?? []).map((site) => ({ id: String(site._id ?? site.id), name: site.display_name ?? site.displayName }))
      );
    } catch (err) {
      setError(err?.response?.data?.error || "The site groups could not be loaded.");
    } finally {
      setLoading(false);
    }
    // the provider rebuilds sendRequest on render; load on mount and on demand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  // ids go back as the server stores them: numbers for old sites
  const ids = (list) => list.map((id) => (/^\d+$/.test(String(id)) ? Number(id) : id));

  const save = async ({ id, name, description, trapSiteIds }) => {
    const data = { name, description, trapSiteIds: ids(trapSiteIds) };
    const res = id
      ? await sendRequest("put")({ url: `/trap-site-group/${id}`, data, devPort: 5005 })
      : await sendRequest("post")({ url: "/trap-site-groups", data, devPort: 5005 });
    await load();
    return res.data;
  };

  const remove = async (id) => {
    await sendRequest("delete")({ url: `/trap-site-group/${id}`, devPort: 5005 });
    await load();
  };

  /** Put a site in exactly `groupIds`, changing only the groups that differ. */
  const setSiteGroups = async (siteId, groupIds) => {
    const key = String(siteId);
    const want = new Set(groupIds.map(String));
    const changes = (await fetchGroups()).filter((g) => want.has(g.id) !== g.trapSiteIds.map(String).includes(key));
    await Promise.all(
      changes.map((g) =>
        sendRequest("put")({
          url: `/trap-site-group/${g.id}`,
          data: {
            trapSiteIds: ids(want.has(g.id) ? [...g.trapSiteIds, key] : g.trapSiteIds.filter((s) => String(s) !== key)),
          },
          devPort: 5005,
        })
      )
    );
    if (changes.length) await load();
  };

  return { groups, sites, loading, error, reload: load, save, remove, setSiteGroups };
};
